import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

type ColorStrategy = 'fill' | 'stroke' | 'both' | 'none';

@Component({
  selector: 'g99-icon-btn',
  templateUrl: './icon-btn.component.html',
  styleUrl: './icon-btn.component.scss',
  standalone: false,
})
export class IconBtnComponent implements OnChanges, AfterViewInit {
  @ViewChild('iconContainer', { static: false })
  public iconContainer!: ElementRef<HTMLElement>;

  @Input() public padding = 0;
  @Input() public icon = ''; // SVG string
  @Input() public color = '';
  @Input() public fillColor = '';
  @Input() public strokeColor = '';
  @Input() public colorStrategy: ColorStrategy = 'both';
  @Input() public backgroundColor = 'transparent';
  @Input() public height = 14;
  @Input() public width = 14;
  @Input() public isLoading = false;
  @Input() public isDisabled = false;
  @Input() public iconLoaderColor = '#5e6367';

  @Output() public iconClick: EventEmitter<Event> = new EventEmitter<Event>();

  private _needsRender = false;

  ngAfterViewInit(): void {
    this._needsRender = true;
    queueMicrotask(() => this._renderSvg());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['icon'] ||
      changes['color'] ||
      changes['fillColor'] ||
      changes['strokeColor'] ||
      changes['colorStrategy'] ||
      changes['height'] ||
      changes['width'] ||
      (changes['isLoading'] && this.isLoading === false)
    ) {
      this._needsRender = true;
      queueMicrotask(() => this._renderSvg());
    }
  }

  protected handleClick(event: Event): void {
    if (this.isDisabled || this.isLoading) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    this.iconClick.emit(event);
  }

  private _renderSvg(): void {
    if (!this._needsRender) {
      return;
    }
    if (!this.iconContainer?.nativeElement) {
      return;
    }
    if (this.isLoading) {
      return;
    }

    // clear previous svg
    const host = this.iconContainer.nativeElement;
    while (host.firstChild) {
      host.removeChild(host.firstChild);
    }

    const svgEl = this._buildSafeSvgElement(this.icon);
    if (!svgEl) {
      this._needsRender = false;
      return;
    }

    // Apply final size
    svgEl.setAttribute('width', String(this.width));
    svgEl.setAttribute('height', String(this.height));
    svgEl.style.width = `${this.width}px`;
    svgEl.style.height = `${this.height}px`;

    host.appendChild(svgEl);
    this._needsRender = false;
  }

  /**
   * Strict whitelist SVG builder:
   * - allows only svg + a small set of shape tags (path, g, circle, rect, line, polyline, polygon)
   * - drops event handlers (on*)
   * - drops href/xlink:href with javascript: or data:
   * - optionally removes style attributes
   */
  private _buildSafeSvgElement(svgString: string): SVGSVGElement | null {
    if (!svgString) {
      return null;
    }

    const doc = new DOMParser().parseFromString(svgString, 'image/svg+xml');
    const root = doc.documentElement;

    if (root.tagName.toLowerCase() !== 'svg') {
      return null;
    }

    const allowedTags = new Set([
      'svg',
      'path',
      'g',
      'circle',
      'rect',
      'line',
      'polyline',
      'polygon',
      'defs',
      'lineargradient',
      'radialgradient',
      'stop',
      'clippath',
      'mask',
      'symbol',
      'image',
      'use',
    ]);

    const allowedAttrs = new Set([
      // common
      'viewbox',
      'width',
      'height',
      'xmlns',
      'id',
      // paint
      'fill',
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'fill-rule',
      'clip-rule',
      'opacity',
      'fill-opacity',
      'stroke-opacity',
      'stop-opacity',
      // geometry
      'd',
      'cx',
      'cy',
      'r',
      'x',
      'y',
      'x1',
      'y1',
      'x2',
      'y2',
      'points',
      'offset',
      'stop-color',
      'gradientunits',
      'gradienttransform',
      'clip-path',
      'mask',
      'style',
      // transforms
      'transform',
    ]);

    const cloneClean = (node: Element): Element | null => {
      const tag = node.tagName.toLowerCase();
      if (!allowedTags.has(tag)) {
        return null;
      }

      const el = document.createElementNS(
        'http://www.w3.org/2000/svg',
        node.tagName
      );

      // copy allowed attributes only (and block on* / href javascript/data)
      for (const attr of Array.from(node.attributes)) {
        const name = attr.name;
        const lname = name.toLowerCase();
        const value = attr.value;

        if (lname.startsWith('on')) {
          continue;
        } // no events
        if (lname === 'style') {
          continue;
        } // optional: drop inline styles
        if (lname === 'href' || lname === 'xlink:href') {
          if (/^\s*(javascript:|data:)/i.test(value)) {
            continue;
          }
        }
        if (
          !allowedAttrs.has(name) &&
          !allowedAttrs.has(lname) &&
          lname !== 'href' &&
          lname !== 'xlink:href'
        ) {
          continue;
        }

        el.setAttribute(name, value);
      }

      // recurse children
      for (const child of Array.from(node.children)) {
        const cleaned = cloneClean(child);
        if (cleaned) {
          el.appendChild(cleaned);
        }
      }
      return el;
    };

    const cleanSvg = cloneClean(root) as SVGSVGElement | null;
    if (!cleanSvg) {
      return null;
    }

    // Apply your color strategy safely
    this._applyColorStrategyToElement(cleanSvg);

    return cleanSvg;
  }

  private _applyColorStrategyToElement(svg: SVGSVGElement): void {
    const fillColorToUse = this.fillColor || this.color;
    const strokeColorToUse = this.strokeColor || this.color;

    const paths = Array.from(
      svg.querySelectorAll('path, circle, rect, line, polyline, polygon')
    ) as SVGElement[];

    if (this.colorStrategy === 'fill' || this.colorStrategy === 'both') {
      if (fillColorToUse) {
        paths.forEach((el) => el.setAttribute('fill', fillColorToUse));
      }
    }

    if (this.colorStrategy === 'stroke' || this.colorStrategy === 'both') {
      if (strokeColorToUse) {
        paths.forEach((el) => el.setAttribute('stroke', strokeColorToUse));
      }
      if (this.colorStrategy === 'stroke') {
        paths.forEach((el) => el.setAttribute('fill', 'none'));
      }
    }

    // If none: do nothing (keeps original)
  }
}
